import { PROFESSIONS } from '@nine/shared';
import { leaf, star } from '../kit';
import { C, type Hex, type Pix } from '../pix';
import { defineIconKind, registerIcons } from '../store';

/** Professions (อาชีพ): a tile in the trade's colour, its tool. */

const PROF_TINT: Readonly<Record<string, Hex>> = {
  woodcutter: '#8a6a3a',
  miner: '#7a7a8a',
  fisher: '#3a8ac8',
  herbalist: '#4ab05a',
  farmer: '#c8a040',
  blacksmith: '#c8602a',
  tailor: '#b05a9a',
  cook: '#e8803a',
};

defineIconKind('profession', {
  label: 'อาชีพ',
  ids: () => Object.keys(PROFESSIONS),
  name: (id) => PROFESSIONS[id as keyof typeof PROFESSIONS]?.name ?? id,
  tint: (id) => PROF_TINT[id] ?? C.gold,
  plate: 'square',
  fallback: () => ({ draw: (p) => star(p, 16, 16, 6, C.paper) }),
});

const handle = (p: Pix, x0: number, y0: number, x1: number, y1: number) =>
  p.thick(x0, y0, x1, y1, '#a8784a', 2);

registerIcons('profession', {
  woodcutter: (p) => {
    handle(p, 8, 28, 22, 6);
    p.poly(
      [
        [17, 6],
        [27, 4],
        [28, 13],
        [21, 13],
      ],
      C.steelMid,
    );
    p.line(27, 5, 28, 12, C.white);
  },
  miner: (p) => {
    handle(p, 10, 28, 20, 8);
    p.path(
      [
        [6, 12],
        [12, 6],
        [20, 5],
        [27, 9],
        [29, 14],
      ],
      C.steelMid,
      2,
    );
    p.px(29, 15, C.white).px(5, 13, C.white);
  },
  fisher: (p) => {
    p.line(5, 28, 24, 4, '#a8784a');
    p.line(24, 4, 26, 18, C.white);
    p.ring(26, 20, 2, 2, C.steelMid, -Math.PI, Math.PI / 2);
    for (let x = 4; x < 29; x++) p.px(x, 25 + Math.round(Math.sin(x / 2)), '#7ac8f0');
  },
  herbalist: (p) => {
    p.poly(
      [
        [6, 16],
        [26, 16],
        [23, 28],
        [9, 28],
      ],
      '#c8a060',
    );
    for (let y = 18; y < 28; y += 3) p.rect(8, y, 17, 1, '#a07a40');
    leaf(p, 12, 16, -0.3, -1, 10, '#5cc04a');
    leaf(p, 17, 16, 0.4, -1, 12, '#7ad85a');
    leaf(p, 21, 16, 1, -0.6, 8, '#5cc04a');
  },
  farmer: (p) => {
    handle(p, 6, 28, 22, 6);
    p.poly(
      [
        [19, 4],
        [28, 7],
        [26, 12],
        [20, 9],
      ],
      C.steelMid,
    );
    for (const x of [8, 13]) p.line(x, 20, x, 27, '#8ac050').line(x - 2, 22, x, 24, '#8ac050');
  },
  blacksmith: (p) => {
    p.poly(
      [
        [4, 22],
        [28, 22],
        [24, 25],
        [22, 29],
        [10, 29],
        [8, 25],
      ],
      '#4a4a58',
    );
    p.rect(4, 21, 24, 2, '#6a6a7a');
    handle(p, 9, 18, 20, 7);
    p.rect(18, 3, 9, 6, C.steelMid);
    star(p, 15, 19, 2, '#ffd060');
  },
  tailor: (p) => {
    p.line(6, 26, 26, 6, C.steel);
    p.ring(25, 7, 1.5, 1.5, C.steelDark);
    p.path(
      [
        [24, 8],
        [20, 14],
        [24, 20],
        [16, 24],
        [12, 20],
        [6, 26],
      ],
      '#ff8ad0',
    );
    p.ell(10, 10, 4, 4, '#e8c8f0');
    p.ring(10, 10, 4, 4, '#b05a9a');
  },
  cook: (p) => {
    p.ell(14, 20, 11, 6, '#3a3640');
    p.ell(14, 18, 9, 3, '#5a5660');
    p.thick(24, 18, 30, 14, C.wood, 2);
    for (const x of [9, 14, 19])
      for (let y = 6; y < 14; y++) p.px(x + Math.round(Math.sin(y / 1.5 + x)), y, C.white);
  },
});
